import SearchIcon from '@mui/icons-material/Search';
import { FormControl, InputAdornment, ListSubheader, MenuItem, Select, SelectProps, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import scssVariables from '../../../../Config.module.scss';
import languageService from '../../../Services/Language/LanguageService';
import { EventActionsEnum, EventCategoriesEnum } from '../../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../../Services/Logging/LoggingService';
import trackingService, { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import InputContainer from './InputContainer';
import styles from './Inputs.module.scss';

export type RichSelectInputOption<T> = {
    optionKey: string;
    value: T;
    label: string;
    title: string;
    subtitle?: string;
    detail?: string;
    description?: string;
    disabled?: boolean;
    isMuted?: boolean;
};

interface IRichSelectInputProps<T> {
    title?: string;
    value?: T;
    options: RichSelectInputOption<T>[];
    handleOptionChange: (value: T) => void;
    disabled?: boolean;
    searchPlaceholder?: string;
    noResultsText?: string;
    track: TrackingEvent;
}

const StyledMuiSelect = styled(Select)<SelectProps>(() => ({
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: `${scssVariables.inputActiveState} !important`,
    },
}));

const menuMaximumHeight = 400;
const searchOptionKey = 'RichSelectInputSearch';
const noResultsOptionKey = 'RichSelectInputNoResults';

const getSearchTokens = (value: string): string[] =>
    value
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .filter((token) => token.length > 0);

const optionMatchesTokens = (option: RichSelectInputOption<unknown>, tokens: string[]): boolean => {
    const searchableText = [option.title, option.subtitle, option.detail, option.description]
        .filter((text) => !!text)
        .join(' ')
        .toLowerCase();

    return tokens.every((token) => searchableText.includes(token));
};

const renderOptionContent = (option: RichSelectInputOption<unknown>): JSX.Element => (
    <div className={styles.input_rich_select_option}>
        <span className={option.isMuted ? styles.input_rich_select_option_title_muted : styles.input_rich_select_option_title}>{option.title}</span>
        {(option.subtitle || option.detail) && (
            <span className={styles.input_rich_select_option_metadata}>
                {option.subtitle && <span className={styles.input_rich_select_option_subtitle}>{option.subtitle}</span>}
                {option.subtitle && option.detail && <span className={styles.input_rich_select_option_separator}>|</span>}
                {option.detail && <span className={styles.input_rich_select_option_detail}>{option.detail}</span>}
            </span>
        )}
        {option.description && <span className={styles.input_rich_select_option_description}>{option.description}</span>}
    </div>
);

// eslint-disable-next-line @typescript-eslint/comma-dangle
const RichSelectInput = <T,>(props: IRichSelectInputProps<T>): JSX.Element => {
    const [searchText, setSearchText] = useState('');

    // Material UI does not allow non-string values, the unique option key bridges that gap and allows any value type.
    const selectedOption = props.options.find((option) => option.value === props.value);

    const { filteredOptions, hasSearchResults } = useMemo(() => {
        const searchTokens = getSearchTokens(searchText);

        if (searchTokens.length === 0) {
            return { filteredOptions: props.options, hasSearchResults: props.options.length > 0 };
        }

        const matchingOptions = props.options.filter((option) => optionMatchesTokens(option, searchTokens));
        const isSelectedOptionMatching = matchingOptions.some((option) => option.optionKey === selectedOption?.optionKey);

        // The selected option stays rendered so the closed dropdown always has a matching option.
        const renderedOptions = selectedOption && !isSelectedOptionMatching ? [...matchingOptions, selectedOption] : matchingOptions;

        return { filteredOptions: renderedOptions, hasSearchResults: matchingOptions.length > 0 };
    }, [props.options, searchText, selectedOption]);

    const handleOptionChange = (optionKey: string): void => {
        const option = props.options.find((x) => x.optionKey === optionKey);

        if (!option || option.disabled === true) {
            return;
        }

        trackingService.track({ ...props.track, eventValue: option.label });

        loggingService.trackEventWithAnalytics(EventActionsEnum.DropdownSelection, EventCategoriesEnum.DropdownSelected, option.optionKey, props.title);

        props.handleOptionChange(option.value);
    };

    return (
        <InputContainer title={languageService.translate(props.title ?? '')}>
            <FormControl className={styles.input_rich_select} fullWidth size="small">
                <StyledMuiSelect
                    disabled={props.disabled}
                    value={selectedOption?.optionKey ?? ''}
                    onChange={(event) => handleOptionChange(event.target.value as string)}
                    renderValue={() => (selectedOption ? renderOptionContent(selectedOption) : '')}
                    onOpen={() =>
                        loggingService.trackEventWithAnalytics(
                            EventActionsEnum.DropdownSelection,
                            EventCategoriesEnum.DropdownOpened,
                            selectedOption?.optionKey ?? '',
                            props.title,
                        )
                    }
                    onClose={() => {
                        setSearchText('');
                        loggingService.trackEventWithAnalytics(EventActionsEnum.DropdownSelection, EventCategoriesEnum.DropdownClosed, '', props.title);
                    }}
                    sx={{
                        backgroundColor: scssVariables.plainWhite,
                        fontFamily: scssVariables.fontFamily,
                        // The selected value renders the same multi line content as the options, so it may not be clipped to a single line.
                        '& .MuiSelect-select': {
                            minHeight: '40px',
                            height: 'auto',
                            padding: '6px 32px 6px 9px',
                            whiteSpace: 'normal',
                            textOverflow: 'clip',
                            display: 'flex',
                            alignItems: 'center',
                        },
                    }}
                    MenuProps={{
                        autoFocus: false,
                        PaperProps: {
                            style: {
                                maxHeight: menuMaximumHeight,
                            },
                        },
                    }}
                >
                    <ListSubheader key={searchOptionKey} className={styles.input_rich_select_search}>
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            value={searchText}
                            placeholder={props.searchPlaceholder}
                            onChange={(event) => setSearchText(event.target.value)}
                            onKeyDown={(event) => {
                                // Material UI's select uses key presses to jump between options, which would swallow the search text.
                                if (event.key !== 'Escape') {
                                    event.stopPropagation();
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </ListSubheader>
                    {filteredOptions.map((option) => (
                        <MenuItem
                            key={option.optionKey}
                            value={option.optionKey}
                            disabled={option.disabled}
                            sx={[
                                { fontFamily: scssVariables.fontFamily, alignItems: 'flex-start', whiteSpace: 'normal' },
                                { '&.Mui-selected': { backgroundColor: scssVariables.stonexSkyBlue } },
                                { '&:hover': { backgroundColor: scssVariables.deactivatedBlockColor } },
                                { '&.Mui-disabled': { opacity: 0.5, cursor: 'not-allowed' } },
                            ]}
                            data-testid={option.optionKey}
                        >
                            {renderOptionContent(option)}
                        </MenuItem>
                    ))}
                    {!hasSearchResults && (
                        <MenuItem key={noResultsOptionKey} disabled className={styles.input_rich_select_no_results}>
                            {props.noResultsText}
                        </MenuItem>
                    )}
                </StyledMuiSelect>
            </FormControl>
        </InputContainer>
    );
};

export default RichSelectInput;
